import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { STATUS_COLORS } from '../lib/constants'
import { getActiveCrew, UNASSIGNED_CREW_LABEL, UNASSIGNED_CREW_VALUE, normalizeCrewName, displayCrewName, crewMatchesColumn, sameCrewColumn } from '../lib/crew'
import { formatDate, toISODateString, addDays, formatTime12, parseHour, getVisibleTimeSlots, isOperatingStartTime, DEFAULT_START_TIME, DEFAULT_END_TIME } from '../lib/dateUtils'
import { logActivity, ACTIONS } from '../lib/activity'
import JobModal from '../components/JobModal'
import JobDetailsModal from '../components/JobDetailsModal'
import PropertyJobsModal from '../components/PropertyJobsModal'
import PropertyModal from '../components/PropertyModal'

const ROW_HEIGHT = 72 // px per hour slot

// bg = even rows, bgAlt = odd rows (slightly lighter), blank = Unassigned column (slightly deeper)
const DAY_COLORS = [
  { bg: '#FDE68A', bgAlt: '#FEF3C7', blank: '#FCD34D' }, // Sunday    — amber/gold
  { bg: '#FCA5A5', bgAlt: '#FEE2E2', blank: '#F87171' }, // Monday    — red
  { bg: '#FED7AA', bgAlt: '#FFEDD5', blank: '#FDBA74' }, // Tuesday   — orange
  { bg: '#86EFAC', bgAlt: '#DCFCE7', blank: '#4ADE80' }, // Wednesday — green
  { bg: '#93C5FD', bgAlt: '#DBEAFE', blank: '#60A5FA' }, // Thursday  — blue
  { bg: '#C4B5FD', bgAlt: '#EDE9FE', blank: '#A78BFA' }, // Friday    — violet
  { bg: '#5EEAD4', bgAlt: '#CCFBF1', blank: '#2DD4BF' }, // Saturday  — teal
]

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(() => {
    const param = new URLSearchParams(window.location.search).get('date')
    if (param) { const d = new Date(param + 'T00:00:00'); if (!isNaN(d)) return d }
    return new Date()
  })
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalInitial, setModalInitial] = useState(null)
  const [crewMembers, setCrewMembers] = useState([])
  const [draggedJob, setDraggedJob] = useState(null)
    const [detailsJob, setDetailsJob] = useState(null)
  const [dayAddonsOk, setDayAddonsOk] = useState(true)
  const [dayAddonsSaving, setDayAddonsSaving] = useState(false)
  const [propEditOpen, setPropEditOpen] = useState(false)
  const [propToEdit, setPropToEdit] = useState(null)
  const [historyProperty, setHistoryProperty] = useState(null)
  const [printMenuOpen, setPrintMenuOpen] = useState(false)
  const [printSelectMode, setPrintSelectMode] = useState(false)
  const [selectedPrintCrews, setSelectedPrintCrews] = useState(new Set())
  const [fitToScreen, setFitToScreen] = useState(true)
  const resizeState = useRef(null)
  const resizeSaveTimer = useRef(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [pickerMonth, setPickerMonth] = useState(currentDate.getMonth())
  const [pickerYear, setPickerYear] = useState(currentDate.getFullYear())
  const [highlightJobId, setHighlightJobId] = useState(() => {
    return new URLSearchParams(window.location.search).get('highlightJob')
  })

  // All columns including Unassigned (stored as Blank / empty / Unassigned)
  const allColumns = [...crewMembers, { id: 'blank', name: UNASSIGNED_CREW_LABEL }]

  // Map crew name -> color (hex) so job tiles can render their assigned color
  const crewColorByName = crewMembers.reduce((acc, c) => {
    if (c.color) acc[c.name] = c.color
    return acc
  }, {})

  // Map crew id -> color/name for property-assigned tech dot/border coloring
  const crewColorById = crewMembers.reduce((acc, c) => {
    if (c.color) acc[c.id] = c.color
    return acc
  }, {})
  const crewNameById = crewMembers.reduce((acc, c) => {
    acc[c.id] = c.name
    return acc
  }, {})

  // Column width: fit-to-screen divides available space, default uses fixed width
  const colWidth = fitToScreen
    ? `${Math.floor((window.innerWidth - 80) / allColumns.length)}px`
    : '144px'

  const dateStr = toISODateString(currentDate)

  const loadJobs = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    try {
      // Load jobs for current date, including Unassigned (null/empty/Blank)
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id, job_date, start_time, end_time, crew_name, status, flag, comments, property_id,
          property:properties(id, name, address, phone, standing_directions, do_not_service, past_due_invoice, assigned_crew_id)
        `)
        .eq('job_date', dateStr)
        .neq('is_deleted', true)
        .neq('status', 'cancelled')
        .order('start_time')
      if (!error) {
        setJobs(data || [])
      }
    } catch (e) {
      console.warn('Could not load jobs:', e.message)
    } finally {
      setLoading(false)
    }
  }, [dateStr])


  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  // Real-time subscription for live updates
  useEffect(() => {
    if (!supabase) return

    const channel = supabase
      .channel('jobs-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jobs',
          filter: `job_date=eq.${dateStr}`
        },
        () => {
          // Reload jobs when any change happens for this date
          loadJobs()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [dateStr, loadJobs])

  // Load crew once on mount
  useEffect(() => {
    loadCrew()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Clear highlight after 10s and strip the URL param so refresh doesn't re-trigger
  useEffect(() => {
    if (!highlightJobId) return
    const timer = setTimeout(() => {
      setHighlightJobId(null)
      const url = new URL(window.location.href)
      url.searchParams.delete('highlightJob')
      window.history.replaceState({}, '', url.pathname + url.search)
    }, 10000)
    return () => clearTimeout(timer)
  }, [highlightJobId])

  const loadCrew = async () => {
    const crew = await getActiveCrew()
    setCrewMembers(crew)
  }

  // Subscribe to crew changes for real-time updates
  useEffect(() => {
    if (!supabase) return

    const crewChannel = supabase
      .channel('crew-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crew'
        },
        () => {
          loadCrew()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(crewChannel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset state when changing dates
  useEffect(() => {
    setDetailsJob(null)
  }, [dateStr])

  // Load day-level addons flag
  useEffect(() => {
    let mounted = true
    const fetchDayAddons = async () => {
      const { data } = await supabase
        .from('daily_settings')
        .select('addons_ok')
        .eq('date', dateStr)
        .maybeSingle()
      if (mounted) setDayAddonsOk(data?.addons_ok ?? true)
    }
    fetchDayAddons()
    return () => { mounted = false }
  }, [dateStr])

  const handleToggleDayAddons = async () => {
    if (dayAddonsSaving) return
    const newVal = !dayAddonsOk
    setDayAddonsOk(newVal)
    setDayAddonsSaving(true)
    await supabase
      .from('daily_settings')
      .upsert({ date: dateStr, addons_ok: newVal }, { onConflict: 'date' })
    setDayAddonsSaving(false)
  }

  // Expand 5am–midnight to cover any earlier job (e.g. stranded 1am) for this day.
  const visibleSlots = useMemo(() => getVisibleTimeSlots(jobs), [jobs])

  const getJobForCell = (crewName, timeValue) => {
    return jobs.find(j => {
      if (!crewMatchesColumn(j.crew_name, crewName)) return false
      return parseHour(j.start_time) === parseHour(timeValue)
    })
  }

  const isCoveredByMultiHourJob = (crewName, slotHour) => {
    return jobs.find(j => {
      if (!crewMatchesColumn(j.crew_name, crewName)) return false
      const startHour = parseHour(j.start_time)
      const endHour = Math.max(startHour + 1, parseHour(j.end_time || `${startHour + 1}:00`))
      return startHour < slotHour && slotHour < endHour
    }) || null
  }

  const getJobDurationHours = (job) => {
    const startHour = parseHour(job.start_time)
    const endHour = Math.max(startHour + 1, parseHour(job.end_time || `${startHour + 1}:00`))
    return endHour - startHour
  }

  const handleCellClick = (crewName, timeSlot) => {
    const existing = getJobForCell(crewName, timeSlot.value)
    if (existing) {
      setDetailsJob(existing)
      return
    }
    // If covered by a multi-hour job, show that job's details
    const covering = isCoveredByMultiHourJob(crewName, parseHour(timeSlot.value))
    if (covering) {
      setDetailsJob(covering)
      return
    }
    const start = isOperatingStartTime(timeSlot.value) ? timeSlot.value : DEFAULT_START_TIME
    const end = isOperatingStartTime(timeSlot.value)
      ? `${String(parseInt(timeSlot.value, 10) + 1).padStart(2, '0')}:00`
      : DEFAULT_END_TIME
    setModalInitial({
      job_date: dateStr,
      start_time: start,
      end_time: end,
      crew_name: normalizeCrewName(crewName),
      status: 'scheduled',
    })
    setModalOpen(true)
  }

  const handleSaveJob = async (form) => {
    const propName = form.property_name || 'Unknown'

    const crewName = normalizeCrewName(form.crew_name)

    if (!supabase) {
      const mockJob = { id: `local-${Date.now()}`, ...form, crew_name: crewName }
      setJobs(prev => {
        const without = prev.filter(j => j.id !== form.id)
        return [...without, mockJob]
      })
      return
    }

    if (form.id) {
      const { error } = await supabase
        .from('jobs')
        .update({
          property_id: form.property_id,
          crew_name: crewName,
          job_date: form.job_date,
          start_time: form.start_time,
          end_time: form.end_time,
          comments: form.comments,
          status: form.status,
          flag: form.flag || 'normal',
          updated_at: new Date().toISOString(),
        })
        .eq('id', form.id)
      if (error) {
        if (error.code === '23505') {
          throw new Error('This time slot is already taken. Someone else may have just booked it.')
        }
        throw error
      }
      await logActivity(ACTIONS.JOB_UPDATED, 'job', form.id, `Updated job at ${propName} for ${crewName}`)
    } else {
      const newJobFields = {
        property_id: form.property_id,
        crew_name: crewName,
        job_date: form.job_date,
        start_time: form.start_time,
        end_time: form.end_time,
        comments: form.comments,
        status: form.status,
        flag: form.flag || 'normal',
      }
      const { data, error } = await supabase.from('jobs').insert([newJobFields]).select().single()
      if (error) {
        if (error.code === '23505') {
          // Slot may be occupied by a soft-deleted ghost row — try to revive it
          const { data: ghost } = await supabase
            .from('jobs')
            .select('id')
            .eq('crew_name', crewName)
            .eq('job_date', form.job_date)
            .eq('start_time', form.start_time)
            .eq('is_deleted', true)
            .maybeSingle()
          if (ghost) {
            const { error: reviveError } = await supabase
              .from('jobs')
              .update({ ...newJobFields, is_deleted: false, updated_at: new Date().toISOString() })
              .eq('id', ghost.id)
            if (reviveError) throw reviveError
            await logActivity(ACTIONS.JOB_CREATED, 'job', ghost.id, `Created job at ${propName} for ${crewName} on ${form.job_date}`)
          } else {
            throw new Error('This time slot is already taken. Someone else may have just booked it.')
          }
        } else {
          throw error
        }
      } else {
        await logActivity(ACTIONS.JOB_CREATED, 'job', data?.id, `Created job at ${propName} for ${crewName} on ${form.job_date}`)
      }
    }
    await loadJobs()
  }

  const handleDeleteJob = async (jobId) => {
    if (!supabase) {
      setJobs(prev => prev.filter(j => j.id !== jobId))
      return
    }

    const job = jobs.find(j => j.id === jobId)
    const propName = job?.property?.name || 'Unknown'

    const { error } = await supabase
      .from('jobs')
      .update({ is_deleted: true, updated_at: new Date().toISOString() })
      .eq('id', jobId)

    if (error) throw error

    await logActivity(ACTIONS.JOB_DELETED, 'job', jobId, `Deleted job at ${propName}`)
    await loadJobs()
  }

  const handleCancelJob = async (jobId) => {
    if (!supabase) {
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'cancelled' } : j))
      return
    }

    const job = jobs.find(j => j.id === jobId)
    const propName = job?.property?.name || 'Unknown'

    const { error } = await supabase
      .from('jobs')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', jobId)

    if (error) throw error

    await logActivity(ACTIONS.JOB_UPDATED, 'job', jobId, `Cancelled job at ${propName}`)
    await loadJobs()
  }

  // Drag and drop handlers
  const handleDragStart = (e, job) => {
    setDraggedJob(job)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', job.id)
  }

  const handleDragOver = (e) => {
    if (!draggedJob) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = async (e, crewName, timeSlot) => {
    e.preventDefault()
    if (!draggedJob) return

    const newStartTime = timeSlot.value
    // Preserve job duration when dragging
    const origStart = parseHour(draggedJob.start_time)
    const origEnd = Math.max(origStart + 1, parseHour(draggedJob.end_time || `${origStart + 1}:00`))
    const durationHours = origEnd - origStart
    const newEndHour = Math.min(24, parseInt(newStartTime) + durationHours)
    const newEndTime = `${String(newEndHour).padStart(2, '0')}:00`

    // Check if there's already a job in this slot
    const existingJob = getJobForCell(crewName, timeSlot.value)
    const destCrew = normalizeCrewName(crewName)

    if (existingJob && existingJob.id !== draggedJob.id) {
      // Move existing job to Unassigned column (stored as Blank)
      const displacedJob = { ...existingJob, crew_name: UNASSIGNED_CREW_VALUE }
      setJobs(prev => prev.map(j => j.id === existingJob.id ? displacedJob : j))

      if (supabase) {
        await supabase
          .from('jobs')
          .update({ crew_name: UNASSIGNED_CREW_VALUE, updated_at: new Date().toISOString() })
          .eq('id', existingJob.id)
      }
    }

    // Update dragged job
    const updatedJob = {
      ...draggedJob,
      crew_name: destCrew,
      job_date: dateStr,
      start_time: newStartTime,
      end_time: newEndTime,
    }

    // Update local state
    setJobs(prev => {
      const without = prev.filter(j => j.id !== draggedJob.id)
      return [...without, updatedJob]
    })

    // Update in database
    if (supabase) {
      const { error } = await supabase
        .from('jobs')
        .update({
          crew_name: destCrew,
          job_date: dateStr,
          start_time: newStartTime,
          end_time: newEndTime,
          updated_at: new Date().toISOString(),
        })
        .eq('id', draggedJob.id)

      if (error) {
        // Rollback optimistic update on error
        await loadJobs()
        if (error.code === '23505') {
          alert('This time slot is already taken. The calendar has been refreshed.')
        } else {
          alert('Failed to move job: ' + error.message)
        }
        setDraggedJob(null)
        return
      }

      const propName = draggedJob.property?.name || 'Unknown'
      await logActivity(ACTIONS.JOB_UPDATED, 'job', draggedJob.id, `Moved job at ${propName} to ${destCrew} at ${formatTime12(newStartTime)}`)
    }

    setDraggedJob(null)
  }

  const handleDragEnd = () => {
    setDraggedJob(null)
  }

  const isToday = toISODateString(currentDate) === toISODateString(new Date())
  const dayColor = DAY_COLORS[currentDate.getDay()]

  const handleJobResizeStart = (e, job) => {
    e.preventDefault()
    e.stopPropagation()
    const startHour = parseHour(job.start_time)
    const originalEndHour = Math.max(startHour + 1, parseHour(job.end_time || `${startHour + 1}:00`))
    // Cap at the start of the next job in the same column
    const maxEnd = jobs
      .filter(j => j.id !== job.id && sameCrewColumn(j.crew_name, job.crew_name))
      .map(j => parseHour(j.start_time))
      .filter(h => h > startHour)
      .reduce((min, h) => Math.min(min, h), 24)
    resizeState.current = { jobId: job.id, startHour, originalEndHour, currentEndHour: originalEndHour, maxEnd }
    const onMove = (moveEvent) => {
      const delta = moveEvent.clientY - e.clientY
      const snapped = Math.round(originalEndHour + delta / ROW_HEIGHT)
      const clamped = Math.max(startHour + 1, Math.min(maxEnd, snapped))
      resizeState.current.currentEndHour = clamped
      setJobs(prev => prev.map(j =>
        j.id === job.id
          ? { ...j, end_time: `${String(clamped).padStart(2, '0')}:00:00` }
          : j
      ))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      if (!resizeState.current) return
      const { jobId, currentEndHour } = resizeState.current
      resizeState.current = null
      const newEndTime = `${String(currentEndHour).padStart(2, '0')}:00:00`
      // Debounce: cancel any pending save and restart the timer
      if (resizeSaveTimer.current) clearTimeout(resizeSaveTimer.current)
      resizeSaveTimer.current = setTimeout(async () => {
        resizeSaveTimer.current = null
        if (supabase) {
          const { error } = await supabase
            .from('jobs')
            .update({ end_time: newEndTime, updated_at: new Date().toISOString() })
            .eq('id', jobId)
          if (error) { await loadJobs(); return }
        }
        await loadJobs()
      }, 500)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentDate(d => addDays(d, -1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              isToday
                ? 'bg-brand-100 text-brand-700'
                : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setCurrentDate(d => addDays(d, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="relative">
            <button
              onClick={() => {
                setPickerMonth(currentDate.getMonth())
                setPickerYear(currentDate.getFullYear())
                setDatePickerOpen(d => !d)
              }}
              className="text-gray-900 font-semibold text-sm sm:text-base hover:text-brand-600 transition-colors"
            >
              {formatDate(currentDate)}
            </button>

            {datePickerOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDatePickerOpen(false)} />
                <div className="absolute left-0 mt-2 z-50 bg-gray-800 text-white rounded-xl shadow-xl p-3 w-64">
                  {/* Month/Year selectors */}
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => {
                        if (pickerMonth === 0) { setPickerMonth(11); setPickerYear(y => y - 1) }
                        else setPickerMonth(m => m - 1)
                      }}
                      className="p-1 rounded hover:bg-gray-700"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <div className="flex gap-2">
                      <select
                        value={pickerMonth}
                        onChange={e => setPickerMonth(Number(e.target.value))}
                        className="bg-gray-700 text-white text-sm rounded px-1 py-0.5"
                      >
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                          <option key={m} value={i}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={pickerYear}
                        onChange={e => setPickerYear(Number(e.target.value))}
                        className="bg-gray-700 text-white text-sm rounded px-1 py-0.5"
                      >
                        {Array.from({ length: 5 }, (_, i) => pickerYear - 2 + i).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => {
                        if (pickerMonth === 11) { setPickerMonth(0); setPickerYear(y => y + 1) }
                        else setPickerMonth(m => m + 1)
                      }}
                      className="p-1 rounded hover:bg-gray-700"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Day-of-week headers */}
                  <div className="grid grid-cols-7 mb-1">
                    {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                      <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
                    ))}
                  </div>

                  {/* Day grid */}
                  <div className="grid grid-cols-7">
                    {(() => {
                      const firstDay = new Date(pickerYear, pickerMonth, 1).getDay()
                      const daysInMonth = new Date(pickerYear, pickerMonth + 1, 0).getDate()
                      const todayStr = toISODateString(new Date())
                      const selectedStr = toISODateString(currentDate)
                      const cells = []
                      for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />)
                      for (let d = 1; d <= daysInMonth; d++) {
                        const cellDate = new Date(pickerYear, pickerMonth, d)
                        const cellStr = toISODateString(cellDate)
                        const isSelected = cellStr === selectedStr
                        const isToday = cellStr === todayStr
                        cells.push(
                          <button
                            key={d}
                            onClick={() => {
                              setCurrentDate(cellDate)
                              setDatePickerOpen(false)
                            }}
                            className={`text-sm rounded-full w-8 h-8 mx-auto flex items-center justify-center transition-colors ${
                              isSelected
                                ? 'bg-brand-500 text-white font-bold'
                                : isToday
                                ? 'border border-brand-400 text-brand-300'
                                : 'hover:bg-gray-700 text-white'
                            }`}
                          >
                            {d}
                          </button>
                        )
                      }
                      return cells
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>

          {loading && (
            <svg className="animate-spin w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Day-level Addons toggle */}
          <button
            onClick={handleToggleDayAddons}
            disabled={dayAddonsSaving}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-60 ${
              dayAddonsOk
                ? 'bg-green-500 text-white border-green-600 hover:bg-green-600'
                : 'bg-red-500 text-white border-red-600 hover:bg-red-600'
            }`}
          >
            {dayAddonsOk ? '✓ Addons OK' : '✕ No Addons'}
          </button>

          {/* Print Dropdown */}
          <div className="relative">
            <button
              onClick={() => setPrintMenuOpen(!printMenuOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span className="hidden sm:inline">Print</span>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {printMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => { setPrintMenuOpen(false); setPrintSelectMode(false); setSelectedPrintCrews(new Set()) }} />
                <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" style={{ minWidth: '180px' }}>

                  {!printSelectMode ? (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">Print by Crew</div>
                      {crewMembers.map(crew => (
                        <button
                          key={crew.id}
                          onClick={() => {
                            window.location.href = `/print/${dateStr}/${crew.id}`
                            setPrintMenuOpen(false)
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {crew.name}
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          window.location.href = `/print/${dateStr}/blank`
                          setPrintMenuOpen(false)
                        }}
                        className="w-full px-3 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-100"
                      >
                        Unassigned
                      </button>
                      <hr className="my-1 border-gray-200" />
                      <button
                        onClick={() => {
                          setPrintSelectMode(true)
                          setSelectedPrintCrews(new Set())
                        }}
                        className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-1.5"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        Select Crews...
                      </button>
                      <button
                        onClick={() => {
                          window.location.href = `/print-all/${dateStr}`
                          setPrintMenuOpen(false)
                        }}
                        className="w-full px-3 py-1.5 text-left text-sm font-medium text-brand-600 hover:bg-brand-50"
                      >
                        Print All Jobs
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">Select Crews</div>
                      {[...crewMembers, { id: 'blank', name: UNASSIGNED_CREW_LABEL }].map(crew => (
                        <label
                          key={crew.id}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPrintCrews.has(crew.name)}
                            onChange={e => {
                              setSelectedPrintCrews(prev => {
                                const next = new Set(prev)
                                if (e.target.checked) next.add(crew.name)
                                else next.delete(crew.name)
                                return next
                              })
                            }}
                            className="rounded border-gray-300 text-brand-600"
                          />
                          {crew.name}
                        </label>
                      ))}
                      <hr className="my-1 border-gray-200" />
                      <div className="px-3 py-2 flex gap-2">
                        <button
                          disabled={selectedPrintCrews.size === 0}
                          onClick={() => {
                            const crews = [...selectedPrintCrews].join(',')
                            window.location.href = `/print-all/${dateStr}?crews=${encodeURIComponent(crews)}`
                            setPrintMenuOpen(false)
                            setPrintSelectMode(false)
                            setSelectedPrintCrews(new Set())
                          }}
                          className="flex-1 px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Print ({selectedPrintCrews.size})
                        </button>
                        <button
                          onClick={() => { setPrintSelectMode(false); setSelectedPrintCrews(new Set()) }}
                          className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
                        >
                          Back
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Fit to Screen Toggle */}
          <button
            onClick={() => setFitToScreen(!fitToScreen)}
            title={fitToScreen ? 'Switch to scrollable view' : 'Fit all crew to screen'}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              fitToScreen
                ? 'bg-brand-100 text-brand-700 border border-brand-300'
                : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            <span className="hidden sm:inline">{fitToScreen ? 'Scrollable' : 'Fit Screen'}</span>
          </button>

          <button
            onClick={() => {
              setModalInitial({
                job_date: dateStr,
                start_time: '08:00',
                end_time: '09:00',
                crew_name: crewMembers[0]?.name || '',
                status: 'scheduled',
              })
              setModalOpen(true)
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Add Job</span>
          </button>
        </div>
      </div>



      {/* Calendar grid */}
      <div className="flex-1 overflow-auto">
        <div className={fitToScreen ? 'w-full' : 'min-w-max'}>
          {/* Header row */}
          <div className="flex sticky top-0 bg-white z-10 border-b border-gray-200 shadow-sm">
            <div className="w-20 flex-shrink-0 border-r border-gray-200" />
            {allColumns.map(col => {
              const colColor = crewColorByName[col.name]
              // Split the trailing designation (e.g. van number "V38") onto its
              // own line so every header wraps identically regardless of column
              // width — otherwise narrow columns wrap some names but not others.
              const spaceIdx = col.name.indexOf(' ')
              const namePart = spaceIdx === -1 ? col.name : col.name.slice(0, spaceIdx)
              const suffixPart = spaceIdx === -1 ? '' : col.name.slice(spaceIdx + 1)
              return (
                <div
                  key={col.id}
                  style={{ width: colWidth, minWidth: fitToScreen ? undefined : colWidth }}
                  className={`flex-shrink-0 flex flex-col items-center justify-center px-3 py-2 text-center border-r border-gray-100 last:border-r-0 ${
                    col.id === 'blank' ? 'bg-gray-50' : ''
                  }`}
                >
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide leading-tight ${
                    col.id === 'blank' ? 'text-gray-400' : 'text-gray-700'
                  }`}>
                    {colColor && (
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: colColor }}
                      />
                    )}
                    <span className="flex flex-col leading-tight">
                      <span>{namePart}</span>
                      {suffixPart && <span>{suffixPart}</span>}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>

          {/* Time rows */}
          {visibleSlots.map((slot, rowIdx) => (
            <div
              key={slot.value}
              className="flex border-b border-gray-100"
              style={{ height: ROW_HEIGHT, backgroundColor: rowIdx % 2 === 0 ? dayColor.bg : dayColor.bgAlt }}
            >
              {/* Time label */}
              <div className="w-20 flex-shrink-0 border-r border-gray-200 flex items-start justify-end pr-3 pt-1.5">
                <span className="text-xs text-gray-400 font-medium">{slot.label}</span>
              </div>

              {/* Column cells (crew + blank) */}
              {allColumns.map(col => {
                const slotHour = parseHour(slot.value)
                const job = getJobForCell(col.name, slot.value)
                const covered = !job && isCoveredByMultiHourJob(col.name, slotHour)
                const colors = job ? STATUS_COLORS[job.status] || STATUS_COLORS.scheduled : null
                const jobDotColor = job ? (crewColorById[job.property?.assigned_crew_id] || null) : null
                const propName = job?.property?.name || job?.property_name || ''
                const propAddr = job?.property?.address || ''
                const isDragging = draggedJob?.id === job?.id
                const isBlank = col.id === 'blank'
                const durationHours = job ? getJobDurationHours(job) : 1
                const tileHeight = durationHours * ROW_HEIGHT - 4

                return (
                  <div
                    key={col.id}
                    onClick={() => handleCellClick(col.name, slot)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, col.name, slot)}
                    style={{
                      width: colWidth,
                      minWidth: fitToScreen ? undefined : colWidth,
                      backgroundColor: isBlank ? dayColor.blank : undefined,
                      position: 'relative',
                      height: ROW_HEIGHT,
                    }}
                    className={`flex-shrink-0 border-r border-gray-100 last:border-r-0 transition-colors ${
                      covered ? 'pointer-events-none' : 'cursor-pointer hover:bg-brand-50/50'
                    } ${draggedJob && !job && !covered ? 'bg-orange-50/30' : ''}`}
                  >
                    {job ? (
                      <div
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, job)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          e.stopPropagation()
                          setDetailsJob(job)
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setModalInitial({ ...job, property_id: job.property_id || '' })
                          setModalOpen(true)
                        }}
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: 2,
                          right: 2,
                          height: tileHeight,
                          zIndex: 5,
                          ...(jobDotColor && !job.property?.do_not_service && !job.property?.past_due_invoice && job.flag !== 'urgent'
                            ? { borderColor: jobDotColor }
                            : {}),
                        }}
                        className={`group rounded-lg border-2 p-2 overflow-hidden ${
                          job.property?.do_not_service
                            ? 'bg-red-100 border-red-400'
                            : job.property?.past_due_invoice
                            ? 'bg-orange-100 border-orange-400'
                            : job.flag === 'urgent'
                            ? 'bg-white border-red-500'
                            : jobDotColor
                            ? 'bg-white'
                            : 'bg-white border-gray-300'
                        } cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50' : ''} ${
                          highlightJobId === job.id ? 'ring-4 ring-yellow-400 ring-offset-2 animate-pulse !bg-yellow-200 !border-yellow-500' : ''
                        }`}
                        title={job.comments ? `${propName}\n${job.comments}` : propName}
                      >
                        {/* Urgent flag icon */}
                        {job.flag === 'urgent' && (
                          <div className="absolute top-1 left-1">
                            <svg className="w-3.5 h-3.5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" />
                            </svg>
                          </div>
                        )}



                        {/* Tech color dot — top right (uses property's assigned tech color) */}
                        {jobDotColor && (
                          <div
                            className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full border border-white shadow-sm"
                            style={{ backgroundColor: jobDotColor }}
                            title={`Assigned to: ${crewNameById[job.property?.assigned_crew_id] || displayCrewName(job.crew_name)}`}
                          />
                        )}

                        {/* Job tile content */}
                        <div className={`flex items-start ${job.flag === 'urgent' ? 'ml-4' : ''}`}>
                          <div className="min-w-0 pr-2">
                            <p className={`text-xs font-semibold leading-tight ${durationHours >= 2 ? 'break-words' : 'truncate'} ${job.property?.do_not_service ? 'text-red-800' : job.property?.past_due_invoice ? 'text-orange-800' : job.flag === 'urgent' ? 'text-red-800' : colors.text}`}>
                              {propName}{job.property?.do_not_service ? ' ⛔' : job.property?.past_due_invoice ? ' ⚠️' : ''}
                            </p>
                            <p className={`text-xs leading-tight ${durationHours >= 2 ? 'break-words' : 'truncate'} opacity-75 ${job.property?.do_not_service ? 'text-red-700' : job.property?.past_due_invoice ? 'text-orange-700' : job.flag === 'urgent' ? 'text-red-700' : colors.text}`}>
                              {propAddr}
                            </p>
                            {job.comments && (
                              <p className={`text-xs leading-tight ${durationHours >= 2 ? 'whitespace-pre-wrap' : 'truncate'} opacity-60 italic ${job.property?.do_not_service ? 'text-red-700' : job.property?.past_due_invoice ? 'text-orange-700' : job.flag === 'urgent' ? 'text-red-700' : colors.text}`}>
                                {job.comments}
                              </p>
                            )}
                            <p className={`text-xs mt-0.5 opacity-60 ${job.property?.do_not_service ? 'text-red-700' : job.property?.past_due_invoice ? 'text-orange-700' : job.flag === 'urgent' ? 'text-red-700' : colors.text}`}>
                              {formatTime12(job.start_time)} – {formatTime12(job.end_time)}
                            </p>
                          </div>
                        </div>

                        {/* Resize handle — bottom of tile, hidden until hover */}
                        <div
                          className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onMouseDown={(e) => handleJobResizeStart(e, job)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={`w-8 h-1 rounded-full opacity-50 ${job.property?.do_not_service ? 'bg-red-700' : job.property?.past_due_invoice ? 'bg-orange-700' : colors.dot ? '' : 'bg-gray-400'}`
                            + (colors?.dot ? ` bg-current` : '')}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ))}

        </div>
      </div>

      <JobModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveJob}
        onDelete={handleDeleteJob}
        initialData={modalInitial}
        crewMembers={crewMembers}
      />

      <JobDetailsModal
        isOpen={!!detailsJob}
        onClose={() => setDetailsJob(null)}
        job={detailsJob}
        onEdit={(job) => {
          setModalInitial({ ...job, property_id: job.property_id || '' })
          setModalOpen(true)
        }}
        onDelete={handleDeleteJob}
        onCancelJob={handleCancelJob}
        onEditProperty={(prop) => {
          setPropToEdit(prop)
          setPropEditOpen(true)
        }}
        onJobUpdated={(jobId, patch) => {
          setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...patch } : j))
          setDetailsJob(prev => prev?.id === jobId ? { ...prev, ...patch } : prev)
        }}
        onViewHistory={(prop) => setHistoryProperty(prop)}
      />

      <PropertyJobsModal
        isOpen={!!historyProperty}
        onClose={() => setHistoryProperty(null)}
        property={historyProperty}
      />

      <PropertyModal
        key={propToEdit?.id || 'new'}
        isOpen={propEditOpen}
        onClose={() => { setPropEditOpen(false); setPropToEdit(null) }}
        initialData={propToEdit}
        onSave={async (form) => {
          const sanitized = {
            ...form,
            unit_count: form.unit_count === '' || form.unit_count === null || form.unit_count === undefined
              ? null
              : Number(form.unit_count),
          }
          if (!sanitized.id) return
          const { error } = await supabase
            .from('properties')
            .update({ ...sanitized, updated_at: new Date().toISOString() })
            .eq('id', sanitized.id)
          if (error) throw error
          await logActivity(ACTIONS.PROPERTY_UPDATED, 'property', sanitized.id, `Updated property: ${sanitized.name}`)
          // Refresh detailsJob so job details modal reflects updated property
          setDetailsJob(prev => prev ? { ...prev, property: sanitized } : null)
          // Reload jobs so calendar picks up any property name changes
          await loadJobs()
        }}
      />
    </div>
  )
}
